import React, { useRef, useState } from 'react'
import Button from 'react-bootstrap/Button'
import FilePreview from './FilePreview'
import './FileUpload.css'

/**
 * Componente para la carga de archivos con drag & drop
 * 
 * @param {String} title - Título del selector de archivos
 * @param {String} dragDropLabel - Texto para el área de arrastre
 * @param {String} buttonText - Texto del botón
 * @param {String} buttonTextPlural - Texto para archivos múltiples
 * @param {String} buttonTextSingular - Texto para archivo único
 * @param {String} buttonIconClass - Clase de icono para el botón
 * @param {Function} updateFiles - Función a llamar cuando se seleccionan archivos
 * @param {Number} maxFileSizeInBytes - Tamaño máximo de archivo en bytes
 * @param {Boolean} showPreview - Mostrar o no la previsualización interna
 * @param {String} previewTitle - Título para la sección de previsualización
 * @param {...Object} otherProps - Otras propiedades para el input file
 */
const FileUpload = ({
	title,
	dragDropLabel = 'Arrastrar a esta área o',
	buttonText = 'Subir',
	buttonTextPlural = 'archivos',
	buttonTextSingular = 'archivo',
	buttonIconClass = 'fas fa-file-upload',
	updateFiles = null,
	maxFileSizeInBytes = 5000000,
	showPreview = true,
	previewTitle = "Archivos seleccionados",
	...otherProps
}) => {
	const fileInputField = useRef(null)
	const [files, setFiles] = useState([])
	const [isDragging, setIsDragging] = useState(false)

	const handleUploadBtnClick = () => {
		fileInputField.current.click()
	}

	const handleNewFileUpload = (e) => {
		const { files: newFiles } = e.target
		if (newFiles.length > 0) {
			// Convert FileList to Array
			const filesArray = Array.from(newFiles)

			// Aplicar filtros si es necesario
			const validFiles = filesArray.filter(file => file.size <= maxFileSizeInBytes)

			// Actualizar estado interno
			if (!otherProps.multiple) {
				// Si es selección única, reemplazar
				setFiles(validFiles)
			} else {
				// Si es múltiple, añadir a los existentes
				setFiles(prev => [...prev, ...validFiles])
			}

			// Notificar al componente padre
			if (updateFiles) {
				updateFiles(validFiles)
			}
		}

		// Limpiar el input para permitir seleccionar el mismo archivo nuevamente
		e.target.value = ''
	}

	const handleRemoveFile = (file, index) => {
		// Remover del estado interno
		setFiles(prevFiles => prevFiles.filter((_, i) => i !== index))

		// Si el archivo tiene una URL de previsualización, liberarla
		if (file.previewUrl) {
			URL.revokeObjectURL(file.previewUrl)
		}
	}

	function handleDragEnter(e) {
		e.preventDefault()
		e.stopPropagation()
		setIsDragging(true)
	}

	function handleDragOver(e) {
		e.preventDefault()
		e.stopPropagation()
	}

	function handleDragLeave(e) {
		e.preventDefault()
		e.stopPropagation()
		setIsDragging(false)
	}

	function handleDrop(e) {
		e.preventDefault()
		e.stopPropagation()
		setIsDragging(false)

		// Simular un evento de selección de archivos
		if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
			handleNewFileUpload({
				target: {
					files: e.dataTransfer.files
				}
			})
		}
	}

	return (
		<>
			{/* Área para seleccionar archivos */}
			<section
				className={`file-upload-container ${isDragging ? 'dragging-over' : ''}`}
				onDragEnter={handleDragEnter}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
			>
				<label className="file-upload-title">{title}</label>
				<div className="upload-content">
					<div className="upload-icon">
						<i className="fas fa-cloud-upload-alt"></i>
					</div>
					<p className="file-upload-dragdrop-text">{dragDropLabel}</p>
					<Button variant="primary" onClick={handleUploadBtnClick} className="upload-button">
						<i className={buttonIconClass} />
						<span> {buttonText} {otherProps.multiple ? buttonTextPlural : buttonTextSingular}</span>
					</Button>
				</div>
				<input
					className="file-upload-file-selector"
					type="file"
					ref={fileInputField}
					title=""
					value=""
					{...otherProps}
					onChange={handleNewFileUpload}
				/>
			</section>

			{/* Previsualización de archivos (opcional) */}
			{showPreview && files.length > 0 && (
				<FilePreview
					files={files}
					onRemove={handleRemoveFile}
					title={previewTitle}
					showFileSize={true}
					emptyMessage="No hay archivos seleccionados."
				/>
			)}
		</>
	)
}

export default FileUpload